import { StateService } from '@pulse/browser-shared';
import { ClientAttributes, Experiment } from '@pulse/common';
import { Observable, Subscription, firstValueFrom } from 'rxjs';
import { PulseSettings } from './settings.service';

interface SettingsWrapped {
    // Legacy API - for backward compatibility
    get<T = any>(
        tag: string,
        settingName: string,
        updateSettingChange?: boolean,
    ):
        | Promise<T>
        | {
              value: T;
              subscribe: (callback: (value: T) => void, settingName: string) => Subscription;
          };

    // Modern API - preferred for new code
    getValue<T = any>(tag: string, settingName: string, defaultValue?: T): Promise<T>;

    observe<T>(tag: string, settingName: string, defaultValue: T): Observable<T>;
    getSegmentsIds(): Promise<string[]>;
    getExperiments(): Promise<Experiment[]>;
}

export class PulseSettingsFactory {
    constructor(
        private settingsService: PulseSettings,
        private stateService: StateService,
    ) {}
    createPulseSettings(
        defaults: Record<string, any> | null | undefined = {},
        remoteSettingEnabled = true,
        attributes: ClientAttributes = {},
    ): SettingsWrapped {
        const settingsService = this.settingsService;

        // Add attributes to state to have them in all events
        if (Object.keys(attributes).length) {
            const newAttributes = Object.fromEntries(
                Object.entries(attributes).map(([key, value]) => [`attr_${key}`, value]),
            );
            const stateData = this.stateService.getStateInternal()['data'] ?? {};
            this.stateService.setState({
                data: {
                    ...stateData,
                    ...newAttributes,
                },
            });
        }

        return {
            // Legacy API - handles both old and new signatures
            async get<T = any>(
                tag: string,
                settingName: string,
                updateSettingChange?: boolean,
            ): Promise<
                | T
                | {
                      value: T;
                      subscribe: (
                          callback: (value: T) => void,
                          settingName: string,
                      ) => Subscription;
                  }
            > {
                const def = defaults?.[settingName];

                if (updateSettingChange === true) {
                    const observable = settingsService.observe(
                        tag,
                        settingName,
                        def,
                        attributes,
                        remoteSettingEnabled,
                    ) as Observable<T>;

                    const initialValue = await firstValueFrom(observable);

                    return {
                        value: initialValue,
                        subscribe: observable.subscribe.bind(observable),
                    };
                } else {
                    return settingsService.get(
                        tag,
                        settingName,
                        def,
                        attributes,
                        remoteSettingEnabled,
                    );
                }
            },

            // Modern API - preferred for new code
            getValue<T = any>(tag: string, settingName: string, defaultValue?: T): Promise<T> {
                const def = defaultValue ?? defaults?.[settingName];

                return settingsService.get(tag, settingName, def, attributes, remoteSettingEnabled);
            },
            observe<T>(tag: string, settingName: string, defaultValue: T): Observable<T> {
                return settingsService.observe(
                    tag,
                    settingName,
                    defaultValue,
                    attributes,
                    remoteSettingEnabled,
                );
            },
            getSegmentsIds(): Promise<string[]> {
                return settingsService.segments(attributes);
            },
            getExperiments(): Promise<Experiment[]> {
                return settingsService.experiments(attributes);
            },
        } as any;
    }
}
